import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface GAConfig {
  enabled: boolean;
  measurement_id: string;
}

export function useGoogleAnalytics() {
  const { data: config } = useQuery({
    queryKey: ['platform_settings', 'google_analytics_config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'google_analytics_config')
        .maybeSingle();
      if (error) throw error;
      return (data?.value as unknown as GAConfig) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!config?.enabled || !config?.measurement_id) return;

    const id = config.measurement_id;
    if (!/^G-[A-Z0-9]+$/i.test(id)) return;

    // Avoid duplicates
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${id}"]`);
    if (existingScript) return;

    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script1.dataset.gaManaged = 'true';

    const script2 = document.createElement('script');
    script2.dataset.gaManaged = 'true';
    script2.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${id}');
    `;

    document.head.appendChild(script1);
    document.head.appendChild(script2);

    return () => {
      script1.remove();
      script2.remove();
    };
  }, [config?.enabled, config?.measurement_id]);
}
